import { h, Component } from 'preact';
import { withTranslation } from 'react-i18next';
import { AppConstant } from '../../constant/AppConstant';
import { getChunkFailure, subscribeChunkFailure } from '../../utils/chunkReload';
import { adoptNewBuild } from '../../services/versionCheckService';
import style from './style.css';

/**
 * Visible replacement for the blank content area a stale route chunk leaves
 * behind (docs/AUDIT-2026-10-15-stability.md, C1).
 *
 * preact-cli's async loader has no error UI: when a route's chunk fails to
 * load the route never mounts and nothing is drawn. utils/chunkReload.js
 * reloads automatically once per build id; when that one attempt is spent it
 * publishes the failure instead, and this notice renders it with a Reload
 * button. The button goes through adoptNewBuild(), never a bare
 * location.reload(), so the navigation is served by the fresh service worker
 * rather than racing its install (CLAUDE.md "Version / deploy freshness").
 *
 * The `data-chunk-error-notice` attribute lets the content area's route-mount
 * observer tell this element apart from a route that rendered.
 */
class ChunkErrorNotice extends Component {
	state = { failure: getChunkFailure(), isReloading: false };

	componentDidMount() {
		this.unsubscribe = subscribeChunkFailure((failure) => this.setState({ failure }));
	}

	componentWillUnmount() {
		if (this.unsubscribe) this.unsubscribe();
	}

	onReload = () => {
		if (this.state.isReloading) return;
		this.setState({ isReloading: true });
		adoptNewBuild();
	};

	render({ t }, { failure, isReloading }) {
		if (!failure) return null;
		return (
			<div class={style.notice} role="alert" data-chunk-error-notice>
				<h3 class={style.title}>
					<i class="fa fa-exclamation-triangle" /> {t(AppConstant.CHUNK_ERROR_TITLE)}
				</h3>
				<p class={style.body}>{t(AppConstant.CHUNK_ERROR_BODY)}</p>
				<button type="button" class="btn btn-primary" disabled={isReloading} onClick={this.onReload}>
					<i class={isReloading ? 'fa fa-refresh fa-spin' : 'fa fa-refresh'} /> {t(AppConstant.APP_UPDATE_RELOAD)}
				</button>
			</div>
		);
	}
}

export { ChunkErrorNotice };
export default withTranslation()(ChunkErrorNotice);
